import React, { useImperativeHandle, forwardRef, useRef } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Circle as CircleIcon,
  Minus as MinusIcon,
  Brush as BrushIcon,
  Edit3 as Edit3Icon,
  Hand as HandIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateCcw as RotateCcwIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Save as SaveIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Ruler as RulerIcon,
  ChevronDown as ChevronDownIcon,
  Tag as TagIcon,
  Square as SquareIcon,
  Search as SearchIcon,
  Trash2 as Trash2Icon,
  Play as PlayIcon,
  Pause as PauseIcon,
} from 'lucide-react';
import {
  getMeasurementNameFromLabel,
  buildViewMeasurementKeysFromViewDef,
  inferDefaultMeasForViewDraw,
} from '../../../../helpers/api/config/config';
import {
  shapesShareBiometrySlot,
  categoryMeasAppliesToShape,
  correctMeasLabelForGeometry,
} from '../utils/segmentationSaveUtils';
import { categoryHasPairedLengthAndPerimeter, getLengthMeasForCategory } from '../../../../helpers/api/config/config';
import { isPresetDrawnShapeForCategory } from '../../../../helpers/api/config/measConfig';
import {
  bpdLineFromEllipse,
  buildDerivedBpdLineFromHcShape,
  buildLinkedBpdHcPatch,
  collectMissingDerivedBpdLines,
  findLinkedBpdLineIds,
  findPairContainingShapeId,
} from '../utils/ellipseBpdUtils';
import { getMeasurementDisplayName } from '../../../../constants/measurementLabels';
import { ImageCanvas } from './ImageCanvas';
import type { DrawingTool, PixelSpacing, Shape } from '../types';
import { useMultiImageShapes } from '../hooks/useMultiImageShapes';
import './drawing-canvas.css';
import { set } from 'lodash';
import { Button } from '../../common/Button';
import { BsFillArrowLeftCircleFill, BsFillArrowRightCircleFill } from 'react-icons/bs';
import { calculateEllipsePerimeter, calculateEllipseArea, calculateLineLength, calculatePolylineLength, calculateRectangleArea } from '../utils/shapeUtils';
import { useCategories } from '../../../../contexts/CategoriesContext';
import { useFilterOptionsOptional } from '../../../../contexts/FilterOptionsContext';
import { Select } from '../../common/Select';

// Cast Lucide icons to avoid "ReactNode is not a valid JSX element" errors (project TS config)
type LucideProps = { size?: number; className?: string; 'aria-hidden'?: boolean };
const Circle = CircleIcon as React.FC<LucideProps>;
const Minus = MinusIcon as React.FC<LucideProps>;
const Brush = BrushIcon as React.FC<LucideProps>;
const Edit3 = Edit3Icon as React.FC<LucideProps>;
const Hand = HandIcon as React.FC<LucideProps>;
const Undo = UndoIcon as React.FC<LucideProps>;
const Redo = RedoIcon as React.FC<LucideProps>;
const ZoomIn = ZoomInIcon as React.FC<LucideProps>;
const ZoomOut = ZoomOutIcon as React.FC<LucideProps>;
const RotateCcw = RotateCcwIcon as React.FC<LucideProps>;
const Eye = EyeIcon as React.FC<LucideProps>;
const EyeOff = EyeOffIcon as React.FC<LucideProps>;
const Save = SaveIcon as React.FC<LucideProps>;
const ChevronLeft = ChevronLeftIcon as React.FC<LucideProps>;
const ChevronRight = ChevronRightIcon as React.FC<LucideProps>;
const Ruler = RulerIcon as React.FC<LucideProps>;
const ChevronDown = ChevronDownIcon as React.FC<LucideProps>;
const Tag = TagIcon as React.FC<LucideProps>;
const Square = SquareIcon as React.FC<LucideProps>;
const Search = SearchIcon as React.FC<LucideProps>;
const Trash2 = Trash2Icon as React.FC<LucideProps>;
const Play = PlayIcon as React.FC<LucideProps>;
const Pause = PauseIcon as React.FC<LucideProps>;

const OPACITY = 1;

/** Tools that are "drawing" tools; when switching between these we keep the active preset label so circle/ellipse get the same label as freehand. */
const DRAWING_TOOLS: DrawingTool[] = ['line', 'circle', 'freehand', 'square'];
const DRAWING_TOOL_SET = new Set(DRAWING_TOOLS);

export interface ImageInput {
  id: string;
  name: string;
  src: string; // base64 or URL
  pixelSpacing?: PixelSpacing; // Optional pixel spacing for this specific image
  pixel_spacing_unit?: string; // 'mm' or 'cm' or undefined
  initialShapes?: Shape[]; // Optional initial shapes to pre-load for this image
}

interface DrawingCanvasProps {
  /** Initial drawing data (shapes) to load */
  initialDrawing?: Shape[];
  /** Callback when user saves the drawing - includes deleted IDs for backend sync */
  onSave?: (
    shapes: Shape[],
    imageId: string,
    imageName: string,
    deletedDbIds: (number | string)[]
  ) => void;
  /** Callback when a shape is added (for real-time sync) */
  onShapeAdded?: (shape: Shape, imageId: string) => void;
  /** Callback when a shape is deleted (for real-time sync) */
  onShapeDeleted?: (shapeId: string, shape: Shape, imageId: string) => void;
  /** Callback when canvas shapes change (live measurement sidebar sync). */
  onShapesChange?: (shapes: Shape[], imageId: string) => void;
  /** Canvas width */
  width?: number;
  /** Canvas height */
  height?: number;
  /** Whether to show the save button */
  showSaveButton?: boolean;
  /** Array of images to display (base64 or URLs) */
  images?: ImageInput[];
  /** Callback when the current image changes */
  onImageChange?: (imageId: string, imageIndex: number) => void;
  /** View code to filter allowed segmentation labels */
  viewCode?: string;
  /** Category IDs to hide on the canvas (use IDs so e.g. Abdominal Biometry and Meas AC match by same id) */
  hiddenCategoryIds?: number[];
  /** Optional: override categoriesByCode from context (usually from CategoriesProvider) */
  categoriesByCode?: Record<string, number>;
  /** When canvas remounts (e.g. after save), start on this image so user stays on same frame */
  initialCurrentImageId?: string;
  /** Bump after refetch to sync current frame shapes from server without remounting (smoother than key change). */
  syncShapesVersion?: number;
  /** Optional playback control rendered inside the viewer (Play/Pause). */
  playbackControl?: {
    isPlaying: boolean;
    onToggle: () => void;
    disabled?: boolean;
    /** FPS for playback; if provided with onFpsChange, an FPS selector is shown next to the play button */
    fps?: number;
    onFpsChange?: (fps: number) => void;
    /** When set (e.g. video native FPS), FPS options are limited to this value for compatibility. */
    maxFps?: number;
  };
  /** When virtualized (e.g. 101 frames in list but 2041 total), pass total so counter/slider show "625 / 2041" not "52 / 101". */
  totalFrameCount?: number;
  /** 1-based global frame index for display when totalFrameCount is set. */
  globalCurrentFrameIndex?: number;
  /** Called when user changes the slider to a global frame index (1-based). */
  onGlobalFrameIndexChange?: (oneBasedIndex: number) => void;
  /** When true, the live video element is shown behind the canvas; canvas draws only shapes (no image layer). */
  videoMode?: boolean;
  /** Intrinsic video size for coordinate mapping when videoMode is true. */
  videoDimensions?: { width: number; height: number } | null;
  /**
   * Read-only mode: hides the edit-mode toggle and the entire annotation-editing toolbar
   * (drawing tools, move/delete, undo/redo, save) so segmentations can be reviewed but not
   * modified. Playback and the show/hide-annotations toggle remain available. Used by the
   * report explainability viewers, where editing is disabled for now.
   */
  readOnly?: boolean;
}

/** Ref interface for external control of DrawingCanvas */
export interface DrawingCanvasRef {
  selectPresetTool: (
    toolType: 'line' | 'circle' | 'freehand' | 'square',
    label: string
  ) => void;
  deleteShapeByLabel: (label: string) => void;
  clearDirtyStates: () => void;
  /** Clear deleted IDs only after successful save so delete_segmentation_ids is not lost */
  clearDeletedDbIds: (imageId?: string) => void;
  /** Assign dbIds to shapes after save so next save sends updates instead of creating duplicates */
  assignDbIdsAfterSave: (
    changedShapes: Shape[],
    savedIds: string[],
    updatedIds: string[]
  ) => void;
  /** PNG of the drawing layer (image + shapes for DICOM frames; shapes-only in video mode). */
  exportViewToPngBlob: () => Promise<Blob | null>;
}

export const DrawingCanvas = forwardRef<DrawingCanvasRef, DrawingCanvasProps>(
  function DrawingCanvas(
    {
      onSave,
      onShapeAdded,
      onShapeDeleted,
      onShapesChange,
      width = 800,
      height = 600,
      showSaveButton = true,
      images,
      onImageChange,
      viewCode,
      hiddenCategoryIds = [],
      categoriesByCode: categoriesByCodeProp,
      initialCurrentImageId,
      syncShapesVersion,
      playbackControl,
      totalFrameCount,
      globalCurrentFrameIndex,
      onGlobalFrameIndexChange,
      videoMode = false,
      videoDimensions = null,
      readOnly = false,
    },
    ref
  ) {
    const exportCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const { t } = useTranslation(['DrawingCanvas', 'GeneralViewer']);
    const { categories, categoriesByCode: categoriesByCodeContext, getColorForCode, getColorForLabel, measLabelToCategoryId, categoryIdToMeasLabel, getMaxStructuresForCategoryId, codeById, labelToMeasName, measGeometryContext, perimeterMeasLabels } = useCategories();
    const categoriesByCode = categoriesByCodeProp ?? categoriesByCodeContext;
    const filterOptions = useFilterOptionsOptional();
    const [hoveredShapeId, setHoveredShapeId] = useState<string | null>(null);
    const viewMeasKeys = useMemo(() => {
      if (!viewCode || !filterOptions?.views?.length) return undefined;
      const codeCandidates = [
        viewCode,
        viewCode.startsWith('VIEW__') ? viewCode : `VIEW__${viewCode}`,
      ];
      const viewDef = filterOptions.views.find((v) =>
        codeCandidates.includes(v.view_code)
      );
      const keys = buildViewMeasurementKeysFromViewDef(categories, viewDef);
      return keys.size > 0 ? keys : undefined;
    }, [viewCode, filterOptions?.views, categories]);
    const viewQualityRuleCodes = useMemo<Set<string> | null>(() => {
      if (!viewCode || !filterOptions?.views?.length) return null;
      const viewDef = filterOptions.views.find((v) => v.view_code === viewCode);
      if (!viewDef) return null;
      const codes = new Set<string>();
      viewDef.quality_rules.forEach((rule) => {
        const code = codeById[rule.category_id];
        if (code) codes.add(code);
      });
      return codes;
    }, [viewCode, filterOptions?.views, codeById]);
    const [selectedTool, setSelectedTool] = useState<DrawingTool>('pan');
    const [zoomLevel, setZoomLevel] = useState(1);
    const [annotationsVisible, setAnnotationsVisible] = useState(true);
    const [isToolDropdownOpen, setIsToolDropdownOpen] = useState(false);
    const [isPresetToolDropdownOpen, setIsPresetToolDropdownOpen] =
      useState(false);
    const toolDropdownRef = useRef<HTMLDivElement>(null);
    const presetDropdownRef = useRef<HTMLDivElement>(null);
    const toolDropdownTriggerRef = useRef<HTMLButtonElement>(null);
    const presetDropdownTriggerRef = useRef<HTMLButtonElement>(null);
    const toolDropdownMenuRef = useRef<HTMLDivElement>(null);
    const presetDropdownMenuRef = useRef<HTMLDivElement>(null);
    const [toolDropdownPosition, setToolDropdownPosition] = useState<{ top: number; left: number } | null>(null);
    const [presetDropdownPosition, setPresetDropdownPosition] = useState<{ top: number; left: number } | null>(null);
    const [presetToolSearchQuery, setPresetToolSearchQuery] = useState('');
    const [activePresetLabel, setActivePresetLabel] = useState<string | null>(
      null
    );
    const [activePresetColor, setActivePresetColor] = useState<string | null>(
      null
    );
    const [isEditMode, setIsEditMode] = useState(false);
    const TOOL_TYPE_MAP = {
      line: Minus,
      circle: Circle,
      freehand: Brush,
      ruler: Ruler,
    };
    const [showLabels, setShowLabels] = useState(true);
    const [activePresetBackendInfo, setActivePresetBackendInfo] = useState<{
  label: string;
  type: string;
  flag_def_id?: number;
  category_id?: number;
} | null>(null);
    const [replacementModal, setReplacementModal] = useState<{
      isOpen: boolean;
      newShapeData: any;
      oldShapeId: string;
      /** When null, the new shape was not added yet (blocked by limit); on Replace we only update the old shape. */
      newShapeId: string | null;
      label: string;
    } | null>(null);
    // State for temporary pan tool activation with Ctrl
    const [isCtrlPressed, setIsCtrlPressed] = useState(false);
    const [toolBeforeCtrl, setToolBeforeCtrl] = useState<DrawingTool | null>(
      null
    );
    //const handleToggleLabels = () => {
    //  setShowLabels(!showLabels);
    //};
    const handleToggleAnnotations = () => {
      const newAnnotationsVisible = !annotationsVisible;
      setAnnotationsVisible(newAnnotationsVisible);

      // If turning off annotations and current tool requires annotations, switch to pan
      if (
        !newAnnotationsVisible &&
        ['line', 'circle', 'square', 'move', 'freehand', 'delete'].includes(selectedTool)
      ) {
        setSelectedTool('pan');
      }
    };

    const handleToggleEditMode = () => {
      const newEditMode = !isEditMode;
      setIsEditMode(newEditMode);

      // When exiting edit mode, switch to pan tool
      if (!newEditMode) {
        setSelectedTool('pan');
      }
    };

    // Check if images are provided
    const hasImages = images && images.length > 0;

    const {
      shapes,
      addShape,
      appendShapes,
      clearDirtyStates,
      updateShape,
      updateShapesBatch,
      selectShape,
      undo,
      redo,
      canUndo,
      canRedo,
      saveToHistory,
      requestSaveToHistory,
      addShapeWithAutoSave,
      deleteShapeWithAutoSave,
      currentImageId,
      currentImageSrc,
      currentImageName,
      currentImageIndex,
      totalImages,
      nextImage,
      previousImage,
      canGoNext,
      canGoPrevious,
      goToImage,
      goToIndex,
      allImages,
      getDeletedDbIds,
      clearDeletedDbIds,
      deleteShapeByLabel,
    } = useMultiImageShapes(images as any, initialCurrentImageId, syncShapesVersion);
    // Notify parent when current image changes
    useEffect(() => {
      if (currentImageId && onImageChange) {
        onImageChange(currentImageId, currentImageIndex - 1); // currentImageIndex is 1-based, convert to 0-based
      }
    }, [currentImageId, currentImageIndex, onImageChange]);

    // Get pixel spacing for the current image (undefined means use pixels as unit)
    const currentPixelSpacing = images?.find(
      (img) => img.id === currentImageId
    )?.pixelSpacing;
    const currentPixelSpacingUnit = images?.find(
      (img) => img.id === currentImageId
    )?.pixel_spacing_unit;

    const ensureMissingDerivedBpdLines = useCallback(
      (currentShapes: Shape[]) => {
        if (!categories.length || !currentShapes.length) return;
        const missing = collectMissingDerivedBpdLines(
          currentShapes as unknown as Array<Record<string, unknown>>,
          categories,
          measLabelToCategoryId,
          currentPixelSpacing
        );
        if (!missing.length) return;
        appendShapes(missing as unknown as Shape[]);
        if (onShapesChange) {
          onShapesChange(
            [...currentShapes, ...(missing as unknown as Shape[])],
            currentImageId
          );
        }
      },
      [
        categories,
        measLabelToCategoryId,
        currentPixelSpacing,
        appendShapes,
        onShapesChange,
        currentImageId,
      ]
    );

    useEffect(() => {
      ensureMissingDerivedBpdLines(shapes);
    }, [currentImageId, categories, shapes, ensureMissingDerivedBpdLines]);

    function backendLabelToDisplayName(label: string): string {
  // Remove "not_visible" suffix (snake_case from backend) or "(not visible)" (already converted)
  const cleanedLabel = label
    .replace(/_?not_?visible$/gi, '') // Remove trailing _not_visible or not_visible
    .replace(/\s*\(not\s*visible\)\s*/gi, '') // Remove (not visible) if already converted
    .replace(/_+$/, '') // Remove any trailing underscores
    .trim();

  return cleanedLabel
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
  const handleShapeAdd = (shape: Parameters<typeof addShape>[0]) => {
  // Use the active preset info if we are currently using a preset tool
  const backendLabel = activePresetBackendInfo?.label || (shape as any).backendLabel;
  const backendType = activePresetBackendInfo?.type || (shape as any).backendType;
  const flag_def_id = activePresetBackendInfo?.flag_def_id || (shape as any).flag_def_id;
  // 1. Assign Default Color for non-preset tools
  const finalColor = activePresetColor || '#fbbf24'; 

  // 2. AUTO-INCREMENTING LOGIC
  let finalLabel = activePresetLabel;
  if (!finalLabel) {
    const toolNames: Record<string, string> = {
      line: 'Line',
      circle: 'Ellipse',
      square: 'Rectangle',
      freehand: 'Path',
      ruler: 'Measurement'
    };
    const baseName = toolNames[shape.type] || 'Shape';
    // Count ALL shapes of this type so new one gets next number (Line 1, Line 2, ...)
    const count = shapes.filter(s => s.type === shape.type).length;
    finalLabel = `${baseName} ${count + 1}`;
  }

  // 3. Type-safe measurement calculation for the Canvas
  const s = shape as any;
  let measurementValue = 0;
  if (shape.type === 'line' || (shape as any).type === 'ruler') {
    measurementValue = calculateLineLength(s.startPoint, s.endPoint, currentPixelSpacing);
  } else if (shape.type === 'circle') {
    measurementValue = calculateEllipsePerimeter(s.radiusX, s.radiusY, currentPixelSpacing, currentPixelSpacingUnit);
  } else if (shape.type === 'square') {
    measurementValue = calculateRectangleArea(s.startPoint, s.endPoint, currentPixelSpacing);
  } else if (shape.type === 'freehand') {
    measurementValue = calculatePolylineLength(s.points, currentPixelSpacing);
  }
  // const finalShape = {
  //   ...shape,
  //   backendLabel: backendLabel,
  //   backendType: backendType,
  //   flag_def_id: flag_def_id,
  //   label: shape.label ? backendLabelToDisplayName(shape.label) : activePresetLabel,
  //   isNew: true,
  //   isDirty: false
  // };
  const isMeasurement = typeof backendLabel === 'string' && backendLabel.startsWith('MEAS__');
  const measName = isMeasurement ? (backendLabel.split(' ')[0] || backendLabel) : null;
  const categoryId =
    measName
      ? measLabelToCategoryId[measName]
      : (activePresetBackendInfo as any)?.category_id ??
        (activePresetBackendInfo as any)?.flag_def_id;
  const segmentType = isMeasurement ? ('measurement' as const) : ('structure' as const);

  const finalShape = {
    ...shape,
    label: shape.label ? backendLabelToDisplayName(shape.label) : finalLabel,
    color: finalColor,
    backendLabel,
    backendType,
    flag_def_id,
    calculatedValue: measurementValue,
    isNew: true,
    isDirty: false,
    isManual: true,
    categoryId: categoryId ?? undefined,
    segmentType,
  };

  // Plain line/ellipse on a biometry view → default to BPD (line) or HC (ellipse).
  if (
    !isMeasurement &&
    !activePresetBackendInfo &&
    viewMeasKeys &&
    (shape.type === 'line' || shape.type === 'circle')
  ) {
    const inferredMeas = inferDefaultMeasForViewDraw(
      viewMeasKeys,
      shape.type,
      perimeterMeasLabels
    );
    if (inferredMeas) {
      const catId = measLabelToCategoryId[inferredMeas];
      if (catId != null) {
        (finalShape as any).categoryId = catId;
        (finalShape as any).backendLabel = inferredMeas;
        (finalShape as any).label = getMeasurementDisplayName(inferredMeas);
        (finalShape as any).segmentType = 'measurement';
      }
    }
  }

  const shapeForSlotCheck = finalShape as any;
  const isMeasShape =
    shapeForSlotCheck.segmentType === 'measurement' ||
    (typeof shapeForSlotCheck.backendLabel === 'string' &&
      shapeForSlotCheck.backendLabel.startsWith('MEAS__'));

  // One shape per biometry slot (BPD line, HC ellipse, …) — AI + manual combined.
  if (isMeasShape) {
    const existingInSlot = shapes.filter((s) =>
      shapesShareBiometrySlot(
        s as any,
        shapeForSlotCheck,
        categoryIdToMeasLabel,
        measLabelToCategoryId,
        labelToMeasName
      )
    );
    if (existingInSlot.length >= 1) {
      setReplacementModal({
        isOpen: true,
        newShapeData: finalShape,
        oldShapeId: existingInSlot[0].id,
        newShapeId: null,
        label: shapeForSlotCheck.label || 'Measurement',
      });
      return;
    }
  } else if (finalShape.categoryId != null) {
    const limit = getMaxStructuresForCategoryId(finalShape.categoryId);
    const existingShapes = shapes.filter((s) =>
      isPresetDrawnShapeForCategory(
        s as any,
        finalShape.categoryId!,
        categories
      )
    );
    if (existingShapes.length >= limit) {
      setReplacementModal({
        isOpen: true,
        newShapeData: finalShape,
        oldShapeId: existingShapes[0].id,
        newShapeId: null,
        label: finalShape.label || 'Structure',
      });
      return;
    }
  }

  proceedWithAddingShape(finalShape);
};
    const proceedWithAddingShape = (shape: any): Shape | undefined => {
      saveToHistory();
      const newShape = addShapeWithAutoSave(shape);
      if (newShape?.type === 'circle' && newShape.categoryId != null) {
        const cat = categories.find((c) => c.id === newShape.categoryId);
        if (categoryHasPairedLengthAndPerimeter(cat)) {
          const lengthMeas = getLengthMeasForCategory(cat);
          if (lengthMeas) {
            const pts = bpdLineFromEllipse(newShape as any);
            addShapeWithAutoSave({
              id: `derived_bpd_${newShape.id}`,
              type: 'line',
              startPoint: pts.startPoint,
              endPoint: pts.endPoint,
              label: getMeasurementDisplayName(lengthMeas),
              backendLabel: lengthMeas,
              categoryId: newShape.categoryId,
              segmentType: 'measurement',
              derivedFromShapeId: newShape.id,
              isManual: true,
              isNew: true,
              isDirty: true,
              color: newShape.color,
            } as any);
          }
        }
      }
      if (onShapeAdded && newShape) {
        onShapeAdded(newShape, currentImageId);
      }
      return newShape;
    };
    const handleShapeUpdate = (
      id: string,
      updates: Parameters<typeof updateShape>[1]
    ) => {
      const shape = shapes.find(s => s.id === id);
      if (!shape) return;
        const merged = { ...(shape as any), ...(updates as any) } as any;
        let newMeasurement = (shape as any)?.calculatedValue || 0;
       
        // Recalculate measurement based on movement
        const st = (shape as any)?.type;
        if (st === 'line' || st === 'ruler') {
          newMeasurement = calculateLineLength(merged.startPoint, merged.endPoint, currentPixelSpacing);
        } else if (st === 'circle') {
          newMeasurement = calculateEllipsePerimeter(merged.radiusX, merged.radiusY, currentPixelSpacing, currentPixelSpacingUnit);
        } else if (st === 'square') {
          newMeasurement = calculateRectangleArea(merged.startPoint, merged.endPoint, currentPixelSpacing);
        } else if (st === 'freehand') {
          newMeasurement = calculatePolylineLength(merged.points, currentPixelSpacing);
        }
       if (updates.label) {
          (updates as any).color = getColorForLabel(updates.label);
          const shapeGeo =
            merged.type === 'circle'
              ? 'ELLIPSE'
              : merged.type === 'line' || merged.type === 'ruler'
                ? 'LINE'
                : (merged.backendType ?? '').toString().toUpperCase();
          const measFromLabel = getMeasurementNameFromLabel(
            updates.label,
            labelToMeasName
          );
          let resolvedMeas: string | null = measFromLabel;
          let resolvedCategoryId: number | undefined =
            (shape as any)?.categoryId ?? undefined;

          if (measFromLabel) {
            const catId = measLabelToCategoryId[measFromLabel];
            if (catId != null) resolvedCategoryId = catId;
          } else {
            const codeKey = String(updates.label)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, '_');
            const catIdFromCode = categoriesByCode[codeKey];
            if (catIdFromCode != null) {
              resolvedCategoryId = catIdFromCode;
              const catMeas = categoryIdToMeasLabel[catIdFromCode];
              if (catMeas) resolvedMeas = catMeas;
            }
          }

          if (resolvedMeas && resolvedCategoryId != null) {
            const cat = categories.find((c) => c.id === resolvedCategoryId);
            resolvedMeas = correctMeasLabelForGeometry(
              resolvedMeas,
              resolvedCategoryId,
              shapeGeo,
              undefined,
              measGeometryContext
            );
            if (
              resolvedMeas &&
              !categoryHasPairedLengthAndPerimeter(cat) &&
              !categoryMeasAppliesToShape(
                resolvedMeas,
                shapeGeo,
                merged.type,
                measGeometryContext
              )
            ) {
              resolvedMeas = null;
            }
          }

          if (resolvedMeas) {
            (updates as any).backendLabel = resolvedMeas;
            (updates as any).label = getMeasurementDisplayName(resolvedMeas);
            if (resolvedCategoryId != null) {
              (updates as any).categoryId = resolvedCategoryId;
            }
          } else if (resolvedCategoryId != null) {
            (updates as any).categoryId = resolvedCategoryId;
            (updates as any).backendLabel = null;
          }
        }
      const dirtyFlag = shape?.isNew ? {} : { isDirty: true };
      const userEditFlag = { isManual: true };
      const measurementUpdates: Record<string, unknown> = {};
      if (st === 'line' || st === 'ruler') {
        measurementUpdates.calculatedValue = newMeasurement;
        measurementUpdates.showOverlayValue = true;
      } else if (st === 'circle') {
        measurementUpdates.calculatedValue = newMeasurement;
        measurementUpdates.showOverlayValue = true;
      }

      const mergedWithMeas = {
        ...merged,
        ...measurementUpdates,
        ...dirtyFlag,
      };
      const primaryPatch: Partial<Shape> = {
        ...(updates as Partial<Shape>),
        ...dirtyFlag,
        ...userEditFlag,
        ...measurementUpdates,
      } as Partial<Shape>;

      const linkedPatches = buildLinkedBpdHcPatch(
        shapes as any[],
        id,
        mergedWithMeas,
        categories,
        measLabelToCategoryId,
        currentPixelSpacing,
        currentPixelSpacingUnit
      );

      const batch = new Map<string, Partial<Shape>>();
      batch.set(id, primaryPatch);
      linkedPatches.forEach((patch, shapeId) => {
        batch.set(shapeId, patch as Partial<Shape>);
      });
      updateShapesBatch(batch);

      if (onShapesChange) {
        const projected = shapes.map((s) => {
          const patch = batch.get(s.id);
          return patch ? ({ ...s, ...patch } as Shape) : s;
        });
        onShapesChange(projected, currentImageId);
      }
    };

    const handleDragStart = () => {
      // Save history when drag starts (current state before drag)
      saveToHistory();
    };

    const handleDragEnd = () => {
      // The final drag state is already in shapes, just save it to history
      saveToHistory();
    };

    const handleShapeDelete = (shapeId: string) => {
      const shapeToDelete = shapes.find((s) => s.id === shapeId);
      const linkedBpdIds = findLinkedBpdLineIds(shapes as any[], shapeId);

      saveToHistory();
      linkedBpdIds.forEach((id) => deleteShapeWithAutoSave(id));
      deleteShapeWithAutoSave(shapeId);

      if (onShapeDeleted && shapeToDelete) {
        onShapeDeleted(shapeId, shapeToDelete, currentImageId);
      }
    };

    const handleZoomIn = () => {
      setZoomLevel((prev) => Math.min(4, prev + 0.05)); // 5% steps
    };

    const handleZoomOut = () => {
      setZoomLevel((prev) => Math.max(0.25, prev - 0.05)); // 5% steps
    };

    const handleZoomReset = () => {
      setZoomLevel(1); // Reset to 100%
    };

    const handleSave = () => {
  if (onSave) {
    const deletedIds = getDeletedDbIds();
    console.log('Preparing to save shapes:', shapes);
    // Map shapes to a unified format that includes calculated measurements
    const finalizedShapes = shapes.map(shape => {
      let measurementValue = 0;
      let measurementValuePixels = 0;
      let measurementType = "";

      // cm for display, pixels for backend storage
      if (shape.type === 'line' || (shape as any).type === 'ruler') {
        measurementValue = calculateLineLength(shape.startPoint, shape.endPoint, currentPixelSpacing);
        measurementValuePixels = calculateLineLength(shape.startPoint, shape.endPoint);
        measurementType = "length";
      } else if (shape.type === 'circle') {
        measurementValue = calculateEllipsePerimeter(shape.radiusX, shape.radiusY, currentPixelSpacing, currentPixelSpacingUnit);
        measurementValuePixels = calculateEllipsePerimeter(shape.radiusX, shape.radiusY);
        measurementType = "circumference";
      } else if (shape.type === 'square') {
        measurementValue = calculateRectangleArea(shape.startPoint, shape.endPoint, currentPixelSpacing);
        measurementValuePixels = calculateRectangleArea(shape.startPoint, shape.endPoint);
        measurementType = "area";
      } else if (shape.type === 'freehand') {
        measurementValue = calculatePolylineLength(shape.points, currentPixelSpacing);
        measurementValuePixels = calculatePolylineLength(shape.points);
        measurementType = "perimeter";
      }

      const areaPixels = shape.type === 'circle'
        ? calculateEllipseArea(shape.radiusX, shape.radiusY)
        : undefined;
      return {
        ...shape,
        calculatedValue: measurementValue,
        calculatedValuePixels: measurementValuePixels,
        ...(areaPixels != null && { area: areaPixels }),
        unit: currentPixelSpacing ? 'cm' : 'px',
        label: shape.label || activePresetLabel,
        lastModified: new Date().toISOString()
      };
    });

    onSave(finalizedShapes, currentImageId, currentImageName, deletedIds);
    // Do NOT clear deletedDbIds here – parent clears after successful save so delete_segmentation_ids is sent
  }
};

    // Keyboard shortcut handler
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        // Prevent shortcuts when typing in input fields
        if (
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement
        ) {
          return;
        }
        
        // Handle Ctrl key for temporary pan tool (only in edit mode)
        if (e.key === 'Control' && !isCtrlPressed && isEditMode) {
          setIsCtrlPressed(true);
          setToolBeforeCtrl(selectedTool);
          setSelectedTool('pan');
          return;
        }

        // Don't handle other shortcuts if Ctrl is pressed (to avoid conflicts)
        if (e.ctrlKey) return;

        // Handle tool shortcuts
        switch (e.key.toLowerCase()) {
          case 'l':
            if (isEditMode) setSelectedTool('line');
            break;
          case 'e':
            if (isEditMode) setSelectedTool('circle');
            break;
          case 'f':
            if (isEditMode) setSelectedTool('freehand');
          if (e.key?.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey && isEditMode) setSelectedTool('square');
            break;
          case 'm':
            if (isEditMode) setSelectedTool('ruler');
            break;
          case 's':
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              handleSave();
            } else if (isEditMode) {
              setSelectedTool('move');
            }
            break;
          case 'd':
            if (isEditMode) setSelectedTool('delete');
            break;
          case 'p':
            setSelectedTool('pan');
            break;
          case 'v':
            handleToggleAnnotations();
            break;
          case 'z':
            if (canUndo && annotationsVisible && isEditMode) {
              undo();
            }
            break;
          case 'y':
            if (canRedo && annotationsVisible && isEditMode) {
              redo();
            }
            break;
          case '=':
          case '+':
            handleZoomIn();
            break;
          case '-':
            handleZoomOut();
            break;
          case '0':
            handleZoomReset();
            break;
          case 'arrowleft':
            if (canGoPrevious) {
              previousImage();
            }
            break;
          case 'arrowright':
            if (canGoNext) {
              nextImage();
            }
            break;
        }
      },
      [
        selectedTool,
        isCtrlPressed,
        canUndo,
        canRedo,
        annotationsVisible,
        isEditMode,
        undo,
        redo,
        handleToggleAnnotations,
        handleZoomIn,
        handleZoomOut,
        handleZoomReset,
        handleSave,
        canGoPrevious,
        canGoNext,
        previousImage,
        nextImage,
        setAnnotationsVisible,
      ]
    );

    const handleKeyUp = useCallback(
      (e: KeyboardEvent) => {
        // Handle Ctrl key release for temporary pan tool
        if (e.key === 'Alt' || e.key === ' ') {
          setAnnotationsVisible(true); // Show
          return;
        }
        if (e.key === 'Control' && isCtrlPressed) {
          setIsCtrlPressed(false);
          if (toolBeforeCtrl) {
            setSelectedTool(toolBeforeCtrl);
            setToolBeforeCtrl(null);
          }
        }
      },
      [isCtrlPressed, toolBeforeCtrl, setAnnotationsVisible]
    );

    // Add keyboard event listeners
    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
      };
    }, [handleKeyDown, handleKeyUp]);

    // Drawing tools configuration (for dropdown)
    const drawingTools = [
      { tool: 'line' as const, icon: Minus, label: t('tools.line'), shortcut: 'L' },
      { tool: 'circle' as const, icon: Circle, label: t('tools.ellipse'), shortcut: 'E' },
      { tool: 'square' as const, icon: Square, label: t('tools.rectangle'), shortcut: 'R' },
      { tool: 'freehand' as const, icon: Brush, label: t('tools.freehand'), shortcut: 'F' },
      { tool: 'ruler' as const, icon: Ruler, label: t('tools.rulerMeasureOnly'), shortcut: 'M' },
    ];

    // Preset tools from backend categories (config/categories): default_tool, display_name, default_color_rgba
    const presetLabeledTools = useMemo(() => {
      const toolTypeMap = { line: Minus, circle: Circle, freehand: Brush, ruler: Ruler, square: Square };
      const sourceTools = categories.map((c) => {
        const tool: 'line' | 'circle' | 'freehand' | 'square' =
          c.default_tool === 'line' || c.default_tool === 'circle' || c.default_tool === 'freehand' || c.default_tool === 'rectangle' || c.default_tool === 'square'
            ? (c.default_tool === 'rectangle' ? 'square' : c.default_tool)
            : 'freehand';
        return {
          tool,
          icon: toolTypeMap[tool] || Brush,
          presetLabel: c.display_name,
          flagCode: c.code,
          category: c.category_group || 'Other',
          color: getColorForCode(c.code),
          flag_def_id: c.id,
        };
      });
      if (!viewCode || !viewQualityRuleCodes) return sourceTools;
      return sourceTools.filter((t) => viewQualityRuleCodes.has(t.flagCode));
    }, [categories, getColorForCode, viewCode, viewQualityRuleCodes]);

  const presetCategories = useMemo(() => {
    const set = new Set(presetLabeledTools.map((t) => t.category).filter(Boolean));
    return Array.from(set).sort();
  }, [presetLabeledTools]);

  const filteredPresetLabeledTools = useMemo(() => {
    const q = presetToolSearchQuery.trim().toLowerCase();
    if (!q) return presetLabeledTools;
    return presetLabeledTools.filter(
      (t) =>
        (t.presetLabel && t.presetLabel.toLowerCase().includes(q)) ||
        (t.flagCode && t.flagCode.toLowerCase().includes(q)) ||
        (t.category && t.category.toLowerCase().includes(q))
    );
  }, [presetLabeledTools, presetToolSearchQuery]);

  const filteredPresetCategories = useMemo(() => {
    const set = new Set(filteredPresetLabeledTools.map((t) => t.category).filter(Boolean));
    return Array.from(set).sort();
  }, [filteredPresetLabeledTools]);

  // Track which labels are already drawn on the current image
    // This is used to disable preset tools that already have a segmentation
    const drawnLabels = useMemo(() => {
      const labels = new Set<string>();
      shapes.forEach((shape) => {
        if (shape.label) {
          // Clean label: remove "Not Visible" suffix and normalize for matching
          const cleanedLabel = shape.label
            .replace(/\s*not\s*visible\s*/gi, '') // Remove "Not Visible" (case-insensitive)
            .trim()
            .toLowerCase();
          labels.add(cleanedLabel);
        }
      });
      return labels;
    }, [shapes]);
    console.log('Drawn labels on current image:', drawnLabels);
    console.log('shapes:', shapes);
    // Get the current drawing tool info
    const currentDrawingTool =
      drawingTools.find((t) => t.tool === selectedTool) || drawingTools[0];
    const CurrentToolIcon = currentDrawingTool.icon;

    // Helper to handle tool selection from the main tool dropdown (Line, Rectangle, Ellipse, Freehand, Ruler).
    // Always clear preset when user picks a tool from this dropdown so the next draw is a plain shape (e.g. "Line 1"), not the previous structure (e.g. Abdo).
    const handleToolSelect = (tool: DrawingTool) => {
      setSelectedTool(tool);
      setIsToolDropdownOpen(false);
      setActivePresetLabel(null);
      setActivePresetBackendInfo(null);
      setActivePresetColor(null);
    };

    // Helper to handle preset tool selection
  const handlePresetToolSelect = (
  tool: DrawingTool,
  presetLabel: string,
  color: string,
  flagCode: string,
  flag_def_id: number 
) => {
  setSelectedTool(tool);
  setActivePresetLabel(presetLabel);
  setActivePresetColor(getColorForCode(flagCode));
  
  const typeMap: Record<string, string> = { 
    'line': 'POLYLINE', 'circle': 'ELLIPSE', 'freehand': 'FREEHAND', 'square': 'POLYGON'
  };
  setActivePresetBackendInfo({
    label: flagCode,
    type: typeMap[tool] || 'POLYLINE',
    flag_def_id: flag_def_id,
    category_id: flag_def_id ?? categoriesByCode?.[flagCode],
  });
  setIsPresetToolDropdownOpen(false);
  setPresetToolSearchQuery('');
};

    // Expose methods via ref for external control
    useImperativeHandle(
      ref,
      () => ({
        selectPresetTool: (
          toolType: 'line' | 'circle' | 'freehand' | 'square',
          label: string
        ) => {
          // Enable edit mode first
          if (!isEditMode) {
            setIsEditMode(true);
          }
          // Set the tool and label
          setSelectedTool(toolType);
          setActivePresetLabel(label);
        },
        deleteShapeByLabel: (label: string) => {
          deleteShapeByLabel(label);
        },
        clearDirtyStates: () => {
          clearDirtyStates();
        },
        clearDeletedDbIds: (imageId?: string) => {
          clearDeletedDbIds(imageId);
        },
        assignDbIdsAfterSave: (
          changedShapes: Shape[],
          savedIds: string[],
          updatedIds: string[]
        ) => {
          let savedIdx = 0;
          let updatedIdx = 0;
          changedShapes.forEach((shape) => {
            const newId =
              shape.dbId != null
                ? updatedIds[updatedIdx++]
                : savedIds[savedIdx++];
            if (newId != null) {
              updateShape(shape.id, { dbId: newId });
            }
          });
        },
        exportViewToPngBlob: () =>
          new Promise<Blob | null>((resolve) => {
            const c = exportCanvasRef.current;
            if (!c || c.width < 2 || c.height < 2) {
              resolve(null);
              return;
            }
            c.toBlob((b) => resolve(b), 'image/png');
          }),
      }),
      [isEditMode, deleteShapeByLabel, shapes, updateShape, clearDirtyStates, clearDeletedDbIds]
    );

    // Measure trigger position when dropdown opens (for portal placement)
    useEffect(() => {
      if (!isToolDropdownOpen) {
        setToolDropdownPosition(null);
        return;
      }
      const el = toolDropdownTriggerRef.current;
      if (!el) return;
      const run = () => {
        const rect = el.getBoundingClientRect();
        setToolDropdownPosition({ top: rect.top, left: rect.left + rect.width / 2 });
      };
      run();
      const t = requestAnimationFrame(run);
      return () => cancelAnimationFrame(t);
    }, [isToolDropdownOpen]);

    useEffect(() => {
      if (!isPresetToolDropdownOpen) {
        setPresetDropdownPosition(null);
        return;
      }
      const el = presetDropdownTriggerRef.current;
      if (!el) return;
      const run = () => {
        const rect = el.getBoundingClientRect();
        setPresetDropdownPosition({ top: rect.top, left: rect.left + rect.width / 2 });
      };
      run();
      const t = requestAnimationFrame(run);
      return () => cancelAnimationFrame(t);
    }, [isPresetToolDropdownOpen]);

    // Close dropdowns when clicking outside (portaled menus are in body so check menu refs too)
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const insideTool = toolDropdownRef.current?.contains(target) || toolDropdownMenuRef.current?.contains(target);
        const insidePreset = presetDropdownRef.current?.contains(target) || presetDropdownMenuRef.current?.contains(target);
        if (insideTool || insidePreset) return;
        if (isToolDropdownOpen) setIsToolDropdownOpen(false);
        if (isPresetToolDropdownOpen) {
          setIsPresetToolDropdownOpen(false);
          setPresetToolSearchQuery('');
        }
      };

      if (isToolDropdownOpen || isPresetToolDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isToolDropdownOpen, isPresetToolDropdownOpen]);
    const drawnLabelCounts = useMemo(() => {
      const counts = new Map<string, number>();
      shapes.forEach((shape) => {
        if (shape.label) {
          // Normalize: lowercase and remove "Not Visible"
          const cleanedLabel = shape.label
            .replace(/\s*not\s*visible\s*/gi, '')
            .trim()
            .toLowerCase();

          counts.set(cleanedLabel, (counts.get(cleanedLabel) || 0) + 1);
        }
      });
      return counts;
    }, [shapes]);
    const handleConfirmReplacement = () => {
      if (!replacementModal) return;
      const existingShape = shapes.find((s) => s.id === replacementModal.oldShapeId);
      const { newShapeData, oldShapeId, newShapeId } = replacementModal;
      const resolvedCategoryId =
        (existingShape as any)?.categoryId ??
        (newShapeData as any)?.categoryId ??
        undefined;
      const merged = {
        ...(existingShape as any),
        ...(newShapeData as any),
        id: oldShapeId,
        isDirty: true,
        isNew: false,
        isManual: true,
        categoryId: resolvedCategoryId ?? (newShapeData as any)?.categoryId,
        dbId: (existingShape as any)?.dbId,
      } as Shape;

      const mergedShapeType = (merged as any).type;
      const measurementUpdates: Record<string, unknown> = {};
      if (mergedShapeType === 'line' || mergedShapeType === 'ruler') {
        measurementUpdates.calculatedValue = calculateLineLength(
          (merged as any).startPoint,
          (merged as any).endPoint,
          currentPixelSpacing
        );
        measurementUpdates.showOverlayValue = true;
      } else if (mergedShapeType === 'circle') {
        measurementUpdates.calculatedValue = calculateEllipsePerimeter(
          (merged as any).radiusX,
          (merged as any).radiusY,
          currentPixelSpacing,
          currentPixelSpacingUnit
        );
        measurementUpdates.showOverlayValue = true;
      }

      const mergedWithMeas = { ...merged, ...measurementUpdates };
      const primaryPatch: Partial<Shape> = {
        ...(newShapeData as Partial<Shape>),
        isDirty: true,
        isNew: false,
        isManual: true,
        categoryId: resolvedCategoryId ?? (newShapeData as any)?.categoryId,
        dbId: (existingShape as any)?.dbId,
        ...measurementUpdates,
      } as Partial<Shape>;

      const cat =
        resolvedCategoryId != null
          ? categories.find((c) => c.id === resolvedCategoryId)
          : undefined;
      const isPairedHcReplace =
        mergedShapeType === 'circle' && cat != null && categoryHasPairedLengthAndPerimeter(cat);

      // Push current state to history before applying replace so undo restores "before"
      saveToHistory();

      if (isPairedHcReplace && cat) {
        const lengthMeas = getLengthMeasForCategory(cat);
        const linkedBpdIds = new Set(findLinkedBpdLineIds(shapes as any[], oldShapeId));
        const pair = findPairContainingShapeId(
          shapes as any[],
          oldShapeId,
          categories,
          measLabelToCategoryId
        );
        const keepBpdId = pair?.bpdLine?.id
          ? String(pair.bpdLine.id)
          : linkedBpdIds.values().next().value ?? null;

        shapes.forEach((shape) => {
          if (shape.id === oldShapeId || shape.id === keepBpdId) return;
          if (shape.type !== 'line') return;
          const meas = String((shape as any).backendLabel ?? '')
            .trim()
            .split(/\s+/)[0];
          const sameCategory =
            (shape as any).categoryId === resolvedCategoryId ||
            measLabelToCategoryId[meas] === resolvedCategoryId;
          const isCategoryBpd =
            sameCategory &&
            (linkedBpdIds.has(shape.id) ||
              (shape as any).derivedFromShapeId === oldShapeId ||
              meas === lengthMeas ||
              meas === 'MEAS__BPD');
          if (isCategoryBpd) {
            deleteShapeWithAutoSave(shape.id);
          }
        });
      }

      if (resolvedCategoryId != null) {
        shapes.forEach((shape) => {
          if (shape.id === oldShapeId) return;
          if (
            shapesShareBiometrySlot(
              shape as any,
              mergedWithMeas as any,
              categoryIdToMeasLabel,
              measLabelToCategoryId,
              labelToMeasName
            )
          ) {
            deleteShapeWithAutoSave(shape.id);
          }
        });
      }

      const linkedPatches = buildLinkedBpdHcPatch(
        shapes as any[],
        oldShapeId,
        mergedWithMeas as any,
        categories,
        measLabelToCategoryId,
        currentPixelSpacing,
        currentPixelSpacingUnit
      );

      const batch = new Map<string, Partial<Shape>>();
      batch.set(oldShapeId, primaryPatch);
      linkedPatches.forEach((patch, shapeId) => {
        batch.set(shapeId, patch as Partial<Shape>);
      });
      updateShapesBatch(batch);

      if (isPairedHcReplace && cat && linkedPatches.size === 0) {
        const lengthMeas = getLengthMeasForCategory(cat);
        if (lengthMeas) {
          const derived = buildDerivedBpdLineFromHcShape(
            mergedWithMeas as any,
            lengthMeas,
            cat.id,
            currentPixelSpacing
          );
          if (derived) {
            addShapeWithAutoSave({
              ...derived,
              isManual: true,
              isNew: true,
              isDirty: true,
              color: (merged as any).color,
            } as any);
          }
        }
      }

      if (newShapeId) {
        deleteShapeWithAutoSave(newShapeId);
      }
      setReplacementModal(null);
      // State update is async; request a history push after next shapes change so redo has the "after" state
      requestSaveToHistory();
    };

    const handleCancelReplacement = () => {
      if (replacementModal?.newShapeId) {
        deleteShapeWithAutoSave(replacementModal.newShapeId);
      }
      setReplacementModal(null);
    };

    // If no images are provided, show disabled state
    if (!hasImages) {
      return (
        <div className="drawing-canvas-container disabled">
          <div className="controls-panel disabled">
            <div className="disabled-overlay">
              <div className="disabled-message">
                <h3>{t('disabled.noImagesLoaded')}</h3>
                <p>{t('disabled.provideImages')}</p>
              </div>
            </div>

            {/* Show disabled controls */}
            <div className="image-navigation disabled">
              <button className="tool-button disabled" disabled>
                <ChevronLeft size={14} />
              </button>
              <div className="image-indicator">
                <span className="image-counter">0 / 0</span>
                <span className="image-name">{t('disabled.noImages')}</span>
              </div>
              <button className="tool-button disabled" disabled>
                <ChevronRight size={14} />
              </button>
            </div>

            <div className="toolbar disabled">
              <div className="tool-group">
                <div className="tool-dropdown disabled">
                  <button className="tool-button disabled" disabled>
                    <Minus size={14} />
                    <ChevronDown size={12} className="dropdown-arrow" />
                  </button>
                </div>
              </div>

              <div className="tool-group">
                <button className="tool-button disabled" disabled title="Edit">
                  <Edit3 size={14} />
                </button>
                <button className="tool-button disabled" disabled title={t('toolbar.moveOnly.short')}>
                  <Hand size={14} />
                </button>
              </div>

              <div className="tool-group">
                <button
                  className="tool-button disabled"
                  disabled
                  title={t('toolbar.toggleAnnotations')}
                >
                  <EyeOff size={14} />
                </button>
                <button className="tool-button disabled" disabled title={t('toolbar.undo.short')}>
                  <Undo size={14} />
                </button>
                <button className="tool-button disabled" disabled title={t('toolbar.redo.short')}>
                  <Redo size={14} />
                </button>
              </div>

              <div className="tool-group">
                <button
                  className="tool-button disabled"
                  disabled
                  title={t('toolbar.zoomIn.short')}
                >
                  <ZoomIn size={14} />
                </button>
                <span className="zoom-level">100%</span>
                <button
                  className="tool-button disabled"
                  disabled
                  title={t('toolbar.zoomOut.short')}
                >
                  <ZoomOut size={14} />
                </button>
                <button
                  className="tool-button disabled"
                  disabled
                  title={t('toolbar.resetZoom.short')}
                >
                  <RotateCcw size={14} />
                </button>
              </div>

              {showSaveButton && (
                <div className="tool-group">
                  <button
                    className="tool-button save-button disabled"
                    disabled
                    title={t('toolbar.save.short')}
                  >
                    <Save size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`drawing-canvas-container relative overflow-hidden${videoMode ? ' drawing-canvas-video-mode' : ''}`}>
        {/* Canvas Area - Centered */}
        <div className="canvas-wrapper">
          <div className="canvas-with-scrollbar relative">
            {/* Canvas layer first (z-0) so overlay buttons rendered after sit on top and receive clicks; wheel works on canvas. */}
            <div className="absolute inset-0 z-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
              {/* In video mode keep pointer-events:none so clicks fall through to the toolbar;
                  the canvas element itself has pointer-events:auto via CSS and handles drawing. */}
              <div style={{ pointerEvents: videoMode ? 'none' : 'auto', width: '100%', height: '100%' }}>
                <ImageCanvas
                  width={width}
                  height={height}
                  selectedTool={selectedTool}
                  shapes={shapes}
                  pixelSpacing={currentPixelSpacing}
                  pixel_spacing_unit={currentPixelSpacingUnit}
                  onShapeAdd={handleShapeAdd}
                  onShapeUpdate={handleShapeUpdate}
                  onShapeSelect={selectShape}
                  onShapeDelete={handleShapeDelete}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  zoomLevel={zoomLevel}
                  onZoomChange={setZoomLevel}
                  annotationsVisible={annotationsVisible}
                  isEditMode={isEditMode}
                  imageSrc={currentImageSrc}
                  onNextImage={(() => {
                    const useGlobalNav = totalFrameCount != null && onGlobalFrameIndexChange != null && globalCurrentFrameIndex != null;
                    const canNext = useGlobalNav ? (globalCurrentFrameIndex as number) < (totalFrameCount as number) : canGoNext;
                    if (!canNext) return undefined;
                    return useGlobalNav
                      ? () => onGlobalFrameIndexChange!((globalCurrentFrameIndex as number) + 1)
                      : nextImage;
                  })()}
                  onPreviousImage={(() => {
                    const useGlobalNav = totalFrameCount != null && onGlobalFrameIndexChange != null && globalCurrentFrameIndex != null;
                    const canPrev = useGlobalNav ? (globalCurrentFrameIndex as number) > 1 : canGoPrevious;
                    if (!canPrev) return undefined;
                    return useGlobalNav
                      ? () => onGlobalFrameIndexChange!((globalCurrentFrameIndex as number) - 1)
                      : previousImage;
                  })()}
                  presetLabel={activePresetLabel}
                  presetColor={activePresetColor}
                  hoveredShapeId={hoveredShapeId} // Pass hover state down
                  onHoverChange={setHoveredShapeId}
                  showLabels={showLabels}
                  hiddenCategoryIds={hiddenCategoryIds}
                  videoMode={videoMode}
                  videoDimensions={videoDimensions}
                  exportCanvasRef={exportCanvasRef}
                />
              </div>
            </div>
            {/* Overlay prev/next buttons — use global nav when available (video virtualized mode) */}
            {(() => {
              const useGlobalNav = totalFrameCount != null && onGlobalFrameIndexChange != null && globalCurrentFrameIndex != null;
              const overlayCanGoPrev = useGlobalNav ? (globalCurrentFrameIndex as number) > 1 : canGoPrevious;
              const overlayCanGoNext = useGlobalNav ? (globalCurrentFrameIndex as number) < (totalFrameCount as number) : canGoNext;
              const overlayPrev = useGlobalNav
                ? () => onGlobalFrameIndexChange!((globalCurrentFrameIndex as number) - 1)
                : previousImage;
              const overlayNext = useGlobalNav
                ? () => onGlobalFrameIndexChange!((globalCurrentFrameIndex as number) + 1)
                : nextImage;
              return (
                <>
                  <div className="absolute top-1/2 left-2 -translate-y-1/2 z-[100] pointer-events-auto">
                    <button
                      type="button"
                      className={`text-white tool-button ${!overlayCanGoPrev ? 'disabled' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (overlayCanGoPrev) overlayPrev();
                      }}
                      disabled={!overlayCanGoPrev}
                      title="Previous frame (←)"
                    >
                      <BsFillArrowLeftCircleFill size={36} className="text-white" />
                    </button>
                  </div>
                  <div className="absolute top-1/2 right-2 -translate-y-1/2 z-[100] pointer-events-auto">
                    <button
                      type="button"
                      className={`text-white tool-button ${!overlayCanGoNext ? 'disabled' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (overlayCanGoNext) overlayNext();
                      }}
                      disabled={!overlayCanGoNext}
                      title="Next frame (→)"
                    >
                      <BsFillArrowRightCircleFill size={36} className="text-white" />
                    </button>
                  </div>
                </>
              );
            })()}
            {/* Play/Pause moved to bottom-controls bar */}
            {/* Edit Label panel: top-left of canvas, avoids DICOM metadata on the right */}
            {isEditMode && selectedTool === 'move' && shapes.find(s => (s as any).isSelected) && (
              <div className="absolute top-3 left-3 z-[60] bg-white dark:bg-gray-800 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-fit">
                <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">Edit Label</div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 text-sm p-1 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    value={shapes.find(s => (s as any).isSelected)?.label || ''}
                    onChange={(e) => {
                      const selectedShape = shapes.find(s => (s as any).isSelected);
                      if (selectedShape) {
                        handleShapeUpdate(selectedShape.id, { label: e.target.value });
                      }
                    }}
                    placeholder="Enter label name..."
                    autoFocus
                  />
                  <button
                    type="button"
                    className="p-1 bg-blue-500 text-white rounded text-xs px-2"
                    onClick={() => {
                      saveToHistory();
                      selectShape(null);
                    }}
                  >
                    Done
                  </button>
                </div>
                <div className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
                  Click the save button to persist changes.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Controls - Unified Bar (z-10 so frame nav is always clickable) */}
        <div className="bottom-controls relative z-10 bg-[var(--dc-bg-secondary)]">
          {/* Play / Pause + FPS — only shown when playbackControl is provided (video mode) */}
         {playbackControl && (
          <div className="tool-group flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!playbackControl.disabled) playbackControl.onToggle();
              }}
              disabled={playbackControl.disabled}
              className="tool-button disabled:opacity-50"
              title={playbackControl.isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            >
              {playbackControl.isPlaying ? <Pause size={14} /> : <Play size={14} />}
            </button>
            
            {playbackControl.fps != null && playbackControl.onFpsChange && (
                <Select
                  // Convert number to string for the native select value
                  value={playbackControl.fps.toString()} 
                  onChange={(e) => {
                    // e is React.ChangeEvent<HTMLSelectElement>
                    const v = Number(e.target.value);
                    if (!isNaN(v) && v > 0) playbackControl.onFpsChange?.(v);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  // We use className to override the default 'w-full' and 'h-8' of your Select component
                  className="!h-7 !w-auto !min-w-[65px] !text-[11px] font-medium"
                  title={t('playback.fps.title')}
                >
                  {([2, 4, 6, 8, 12, 16, 24, 30] as const)
                    .filter((n) => (playbackControl.maxFps ?? Infinity) >= n)
                    .map((n) => (
                      <option key={n} value={n.toString()}>
                        {t('playback.fps.option', { count: n })}
                      </option>
                    ))}
                </Select>
              )}
          </div>
        )}
          {/* Image Navigation */}
          
          <div className="tool-group flex items-center gap-1">
            {(() => {
              const useGlobalNav = totalFrameCount != null && onGlobalFrameIndexChange != null && globalCurrentFrameIndex != null;
              const effectiveCanGoPrev = useGlobalNav ? globalCurrentFrameIndex > 1 : canGoPrevious;
              const effectiveCanGoNext = useGlobalNav ? globalCurrentFrameIndex < (totalFrameCount ?? 0) : canGoNext;
              const onPrev = useGlobalNav ? () => onGlobalFrameIndexChange!(globalCurrentFrameIndex - 1) : previousImage;
              const onNext = useGlobalNav ? () => onGlobalFrameIndexChange!(globalCurrentFrameIndex + 1) : nextImage;
              return (
                <>
                  <button
                    type="button"
                    className={`tool-button ${!effectiveCanGoPrev ? 'disabled' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (effectiveCanGoPrev) onPrev();
                    }}
                    disabled={!effectiveCanGoPrev}
                    title={t('nav.previousFrame')}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <form
                    className="image-indicator"
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 2, padding: '0 4px', cursor: 'text' }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      const inp = (e.currentTarget.querySelector('input') as HTMLInputElement);
                      const val = parseInt(inp.value, 10);
                      const max = totalFrameCount ?? totalImages;
                      if (!isNaN(val) && val >= 1 && val <= max) {
                        if (onGlobalFrameIndexChange && totalFrameCount != null) onGlobalFrameIndexChange(val);
                        else goToIndex(val - 1);
                      }
                      inp.blur();
                    }}
                  >
                    <input
                      type="number"
                      min={1}
                      max={totalFrameCount ?? totalImages}
                      defaultValue={totalFrameCount != null && globalCurrentFrameIndex != null ? globalCurrentFrameIndex : currentImageIndex}
                      key={totalFrameCount != null && globalCurrentFrameIndex != null ? globalCurrentFrameIndex : currentImageIndex}
                      className="image-counter frame-jump-input"
                      title={t('nav.jumpToFrame')}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value, 10);
                        const max = totalFrameCount ?? totalImages;
                        if (!isNaN(val) && val >= 1 && val <= max) {
                          if (onGlobalFrameIndexChange && totalFrameCount != null) onGlobalFrameIndexChange(val);
                          else goToIndex(val - 1);
                        }
                      }}
                    />
                    <span className="image-counter select-none pointer-events-none" style={{ opacity: 0.55 }}>
                      / {totalFrameCount ?? totalImages}
                    </span>
                  </form>
                  <button
                    type="button"
                    className={`tool-button ${!effectiveCanGoNext ? 'disabled' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (effectiveCanGoNext) onNext();
                    }}
                    disabled={!effectiveCanGoNext}
                    title={t('nav.nextFrame')}
                  >
                    <ChevronRight size={14} />
                  </button>
                </>
              );
            })()}
          </div>

          {/* Image Slider */}
          {(totalFrameCount != null ? totalFrameCount > 1 : totalImages > 1) && (
            <div className="tool-group">
              <input
                type="range"
                min="1"
                max={totalFrameCount ?? totalImages}
                value={totalFrameCount != null && globalCurrentFrameIndex != null ? globalCurrentFrameIndex : currentImageIndex}
                onChange={(e) => {
                  const oneBased = parseInt(e.target.value, 10);
                  if (onGlobalFrameIndexChange && totalFrameCount != null) {
                    onGlobalFrameIndexChange(oneBased);
                  } else {
                    goToIndex(oneBased - 1);
                  }
                }}
                className="image-slider"
                title={totalFrameCount != null && globalCurrentFrameIndex != null
                  ? t('nav.frameOf', { index: globalCurrentFrameIndex, total: totalFrameCount })
                  : t('nav.imageOf', { index: currentImageIndex, total: totalImages })}
              />
            </div>
          )}

          {/* Edit Mode Toggle */}
          {!readOnly && (
            <div className="tool-group">
              <button
                className={`tool-button ${isEditMode ? 'active' : ''}`}
                onClick={handleToggleEditMode}
                title={isEditMode ? t('toolbar.exitEditMode') : t('toolbar.enterEditMode')}
              >
                <Edit3 size={14} />
              </button>
            </div>
          )}

          {/* Show/Hide Annotations */}
          <div className="tool-group">
            <button
              className={`tool-button ${annotationsVisible ? 'active' : ''}`}
              onClick={handleToggleAnnotations}
              title={
                annotationsVisible
                  ? t('toolbar.hideAnnotations')
                  : t('toolbar.showAnnotations')
              }
            >
              {annotationsVisible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          </div>

          {/* Full toolbar - only visible in edit mode (never in read-only mode) */}
          {!readOnly && isEditMode && (
            <>
              {/* Drawing Tools Dropdown */}
              <div className="tool-group">
                <div className="tool-dropdown" ref={toolDropdownRef}>
                  <button
                    ref={toolDropdownTriggerRef}
                    type="button"
                    className={`tool-button ${drawingTools.some((t) => t.tool === selectedTool) ? 'active' : ''} ${!annotationsVisible && selectedTool !== 'ruler' ? 'disabled' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsToolDropdownOpen(!isToolDropdownOpen);
                    }}
                    disabled={!annotationsVisible && selectedTool !== 'ruler'}
                    title={`${currentDrawingTool.label} (${currentDrawingTool.shortcut})`}
                  >
                    <CurrentToolIcon size={14} />
                    <ChevronDown size={10} className="dropdown-arrow" />
                  </button>

                  {isToolDropdownOpen && toolDropdownPosition &&
                    createPortal(
                      <div
                        ref={toolDropdownMenuRef}
                        className="tool-dropdown-menu tool-dropdown-menu-portal"
                        style={{
                          position: 'fixed',
                          left: (() => {
                            if (typeof window === 'undefined') return toolDropdownPosition.left;
                            const menuHalf = 100;
                            const w = window.innerWidth;
                            return Math.max(menuHalf, Math.min(toolDropdownPosition.left, w - menuHalf));
                          })(),
                          top: toolDropdownPosition.top,
                          transform: 'translate(-50%, -100%) translateY(-8px)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {drawingTools.map(
                          ({ tool, icon: Icon, label, shortcut }) => {
                            const isRulerTool = tool === 'ruler';
                            const isDisabled =
                              !annotationsVisible && !isRulerTool;
                            const isSelected = selectedTool === tool;

                            return (
                              <button
                                key={tool}
                                type="button"
                                className={`tool-dropdown-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => handleToolSelect(tool)}
                                disabled={isDisabled}
                              >
                                <Icon size={12} />
                                <span className="tool-label">{label}</span>
                                <span className="tool-shortcut">{shortcut}</span>
                              </button>
                            );
                          }
                        )}
                      </div>,
                      document.body
                    )}
                </div>
              </div>

              {/* Preset Labeled Tools Dropdown */}
              <div className="tool-group">
                <div className="tool-dropdown" ref={presetDropdownRef}>
                  <button
                    ref={presetDropdownTriggerRef}
                    type="button"
                    className={`tool-button ${activePresetLabel ? 'active' : ''} ${!annotationsVisible ? 'disabled' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPresetToolDropdownOpen(!isPresetToolDropdownOpen);
                    }}
                    disabled={!annotationsVisible}
                    title={
                      activePresetLabel
                        ? t('presetTools.titleWithPreset', { preset: activePresetLabel })
                        : t('presetTools.title')
                    }
                  >
                    <Tag size={14} />
                    <ChevronDown size={10} className="dropdown-arrow" />
                  </button>

                  {isPresetToolDropdownOpen && presetDropdownPosition &&
                    createPortal(
                      <div
                        ref={presetDropdownMenuRef}
                        className="tool-dropdown-menu preset-tools-menu tool-dropdown-menu-portal"
                        style={{
                          position: 'fixed',
                          left: (() => {
                            if (typeof window === 'undefined') return presetDropdownPosition.left;
                            // Preset menu can be ~280–320px wide; keep full menu in view (center clamp so left edge >= 0)
                            const menuHalf = 180;
                            const w = window.innerWidth;
                            return Math.max(menuHalf, Math.min(presetDropdownPosition.left, w - menuHalf));
                          })(),
                          top: presetDropdownPosition.top,
                          transform: 'translate(-50%, -100%) translateY(-8px)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="preset-tools-search">
                          <Search size={14} className="preset-tools-search-icon" aria-hidden />
                          <input
                            type="text"
                            value={presetToolSearchQuery}
                            onChange={(e) => setPresetToolSearchQuery(e.target.value)}
                            placeholder={t('presetTools.search.placeholder')}
                            className="preset-tools-search-input"
                            aria-label={t('presetTools.search.aria')}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {filteredPresetCategories.length === 0 ? (
                          <div className="preset-tools-no-results">
                            {t('presetTools.search.noMatches', { query: presetToolSearchQuery })}
                          </div>
                        ) : null}
                        {filteredPresetCategories.map((category) => {
                          const categoryTools = filteredPresetLabeledTools.filter(
                            (t) => t.category === category
                          );
                          if (categoryTools.length === 0) return null;
                          const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
                          const translatedCategoryLabel = t(`presetTools.category.${category}`, { defaultValue: categoryLabel });

                          return (
                            <div key={category} className="preset-category-group">
                              <div className="category-header">{translatedCategoryLabel}</div>
                              {categoryTools?.map((presetTool, idx) => {
                                const isSelected =
                                  activePresetLabel === presetTool.presetLabel &&
                                  selectedTool === presetTool.tool;
                                const Icon = presetTool.icon || Brush;
                                const rawFlagCode = String((presetTool as any).flagCode ?? '');
                                const normalizedFlagCode = rawFlagCode
                                  .replace(/^QUAL_FLAG__/, '')
                                  .replace(/_(segment_polygon|box_segment|segment_box|segment|polygon|box)$/i, '')
                                  .trim()
                                  .toLowerCase()
                                  .replace(/\s+/g, '_');
                                const translatedPresetLabel =
                                  normalizedFlagCode
                                    ? t(`qualityFlag.${normalizedFlagCode}`, { ns: 'GeneralViewer', defaultValue: presetTool.presetLabel })
                                    : presetTool.presetLabel;

                                const drawnShapes = shapes.filter((s) =>
                                  isPresetDrawnShapeForCategory(
                                    s as any,
                                    presetTool.flag_def_id,
                                    categories
                                  )
                                );
                                const structureCount = drawnShapes.length;
                                const userCount = drawnShapes.filter(
                                  (s) => (s as any).isManual === true
                                ).length;
                                const aiCount = drawnShapes.filter(
                                  (s) => (s as any).isManual === false
                                ).length;
                                const maxAllowed = getMaxStructuresForCategoryId(
                                  presetTool.flag_def_id
                                );
                                const limitReached = structureCount >= maxAllowed;
                                return (
                                  <button
                                    key={`${presetTool.presetLabel}-${presetTool.tool}-${idx}`}
                                    type="button"
                                    className={`tool-dropdown-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() =>
                                      handlePresetToolSelect(
                                        presetTool.tool,
                                        presetTool.presetLabel,
                                        presetTool.color,
                                        presetTool.flagCode,
                                        presetTool.flag_def_id
                                      )
                                    }
                                    title={`Draw ${presetTool.presetLabel} (max ${maxAllowed})`}
                                  >
                                    <Icon size={12} />
                                    <span className="tool-label">
                                      {translatedPresetLabel}
                                    </span>

                                    {structureCount > 0 && (
                                      <span
                                        className="drawn-indicator"
                                        style={{ marginLeft: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}
                                      >
                                        {userCount > 0 && (
                                          <span title={t('presetTools.drawnBy.you')} style={{ color: '#2563eb', fontSize: 10 }}>
                                            👤{userCount > 1 ? `×${userCount}` : ''}
                                          </span>
                                        )}
                                        {aiCount > 0 && (
                                          <span title={t('presetTools.drawnBy.ai')} style={{ color: '#7c3aed', fontSize: 10 }}>
                                            🤖{aiCount > 1 ? `×${aiCount}` : ''}
                                          </span>
                                        )}
                                        {/* ✓ when category limit is reached (AI or manual) */}
                                        {limitReached && (
                                          <span style={{ color: '#10b981', fontSize: 10 }}>✓</span>
                                        )}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>,
                      document.body
                    )}
                </div>
              </div>

              {/* Edit (resize handles + move), Delete, Pan (hand: move structure only, no handles) */}
              <div className="tool-group">
                <button
                  className={`tool-button ${selectedTool === 'move' ? 'active' : ''} ${!annotationsVisible ? 'disabled' : ''}`}
                  onClick={() => setSelectedTool('move')}
                  disabled={!annotationsVisible}
                  title={t('toolbar.edit')}
                >
                  <Edit3 size={14} />
                </button>
                <button
                  className={`tool-button ${selectedTool === 'delete' ? 'active' : ''} ${!annotationsVisible ? 'disabled' : ''}`}
                  onClick={() => setSelectedTool('delete')}
                  disabled={!annotationsVisible}
                  title={t('toolbar.delete')}
                >
                  <Trash2 size={14} />
                </button>
                <button
                  className={`tool-button ${selectedTool === 'pan' ? 'active' : ''} ${!annotationsVisible ? 'disabled' : ''}`}
                  onClick={() => setSelectedTool('pan')}
                  disabled={!annotationsVisible}
                  title={t('toolbar.moveOnly')}
                >
                  <Hand size={14} />
                </button>
              </div>

              {/* Undo/Redo */}
              <div className="tool-group">
                <button
                  className={`tool-button ${!canUndo || !annotationsVisible ? 'disabled' : ''}`}
                  onClick={annotationsVisible ? undo : undefined}
                  disabled={!canUndo || !annotationsVisible}
                  title={t('toolbar.undo')}
                >
                  <Undo size={14} />
                </button>
                <button
                  className={`tool-button ${!canRedo || !annotationsVisible ? 'disabled' : ''}`}
                  onClick={annotationsVisible ? redo : undefined}
                  disabled={!canRedo || !annotationsVisible}
                  title={t('toolbar.redo')}
                >
                  <Redo size={14} />
                </button>
              </div>

              {/* Zoom Controls */}
              <div className="tool-group zoom-controls">
                <button
                  className={`tool-button ${zoomLevel <= 0.25 ? 'disabled' : ''}`}
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.25}
                  title={t('toolbar.zoomOut')}
                >
                  <ZoomOut size={14} />
                </button>
                <div className="zoom-display">
                  {Math.round(zoomLevel * 100)}%
                </div>
                <button
                  className={`tool-button ${zoomLevel >= 4 ? 'disabled' : ''}`}
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 4}
                  title={t('toolbar.zoomIn')}
                >
                  <ZoomIn size={14} />
                </button>
                <button
                  className={`tool-button ${zoomLevel === 1 ? 'disabled' : ''}`}
                  onClick={handleZoomReset}
                  disabled={zoomLevel === 1}
                  title={t('toolbar.resetZoom')}
                >
                  <RotateCcw size={12} />
                </button>
              </div>

              {/* Save Button */}
              {showSaveButton && (
                <div className="tool-group">
                  <button
                    className="tool-button save-button"
                    onClick={handleSave}
                    title={t('toolbar.save')}
                  >
                    <Save size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        {replacementModal?.isOpen && (
          <div className="absolute top-4 left-0 right-0 z-[100] flex justify-center px-4 pointer-events-none">
            <div
              className="pointer-events-auto w-full
            bg-white/90 border-orange-100 shadow-[0_10px_40px_rgba(0,0,0,0.1)] 
            dark:bg-gray-800 dark:border-orange-900/30 dark:shadow-[0_10px_40px_rgba(0,0,0,0.4)]
            backdrop-blur-md border rounded-xl p-3 flex items-center gap-4 ring-1 ring-black/5 dark:ring-white/5 
            animate-in fade-in slide-in-from-top-4 duration-300
          "
            >
              {/* Icon - Adapts to Dark Mode */}
              <div className="flex-shrink-0 w-10 h-10 bg-orange-50 dark:bg-orange-500/10 rounded-full flex items-center justify-center text-orange-500 dark:text-orange-400">
                <Tag size={20} />
              </div>

              {/* Message Text */}
              <div className="flex-grow">
                <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">
                  Replace {replacementModal.label}?
                </h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium leading-normal mt-0.5">
                  Limit reached. Drawing this will replace the oldest version.
                </p>
              </div>

              {/* Buttons - Custom Component handles dark mode automatically */}
              <div className="flex items-center gap-2 border-l pl-4 border-slate-100 dark:border-slate-800">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (replacementModal?.newShapeId) {
                      deleteShapeWithAutoSave(replacementModal.newShapeId);
                    }
                    setReplacementModal(null);
                  }}
                  className="font-bold whitespace-nowrap"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleConfirmReplacement}
                  className="font-bold px-4 h-8 whitespace-nowrap"
                >
                  Replace
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);
